/**
 * Workspace shell for /apps/:appId.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Header                                        │
 *   ├──────────┬──────────────────────────────────┤
 *   │ Sidebar  │  <Outlet /> — section pages       │
 *   └──────────┴──────────────────────────────────┘
 *
 * Loads the app metadata + organisation row once and provides them via
 * WorkspaceContext so child sections don't refetch.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import Header from '../components/Header';
import Sidebar from '../components/workspace/Sidebar';
import AppTour from '../components/workspace/AppTour';
import NotificationBell from '../components/NotificationBell';
import { useApi } from '../lib/api';
import { WorkspaceContext, useWorkspace } from '../lib/workspace';
import { useTheme } from '../lib/ThemeContext';
import '../styles/Dashboard.css';
import '../styles/workspace.css';

// Shared Zoho Desk helpdesk (also used by the HRMS product).
const SUPPORT_TICKET_URL = 'https://greatleapsupport.zohodesk.in/portal/en/signin';

function WorkspaceHeader({ searchQuery, setSearchQuery, appName }) {
  const { user } = useUser();
  const { theme, toggleTheme } = useTheme();
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);
  const initials = user?.firstName
    ? `${user.firstName.charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase()
    : (user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || '?').charAt(0).toUpperCase();

  const [results, setResults]   = useState(null); // null = closed, {} = open
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // Profile menu — admins also get Organisation Setup + Users here
  const { myRole } = useWorkspace();
  const isAdmin = myRole === 'admin' || myRole === 'super_admin';
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    if (!profileOpen) return undefined;
    const onDown = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setProfileOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [profileOpen]);
  const goTo = (path) => { setProfileOpen(false); navigate(path); };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setResults(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(debounceRef.current);
    if (!q.trim() || q.trim().length < 2) { setResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.globalSearch(appId, q.trim());
        setResults(data);
      } catch (_) { setResults({ tasks: [], projects: [], people: [] }); }
      finally { setSearching(false); }
    }, 300);
  };

  const clearSearch = () => { setSearchQuery(''); setResults(null); };

  const handleResultClick = (type, item) => {
    clearSearch();
    if (type === 'task')    navigate(`/apps/${appId}/tasks/${item.id}`);
    if (type === 'project') navigate(`/apps/${appId}/projects/${item.id}`);
  };

  const hasResults = Boolean(results && (results.tasks?.length || results.projects?.length || results.people?.length));
  const noResults  = Boolean(results && !hasResults);

  const personName = (p) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;

  const appNameLenClass = !appName ? '' : appName.length > 24 ? ' wh-app-name--xs' : appName.length > 16 ? ' wh-app-name--sm' : '';

  return (
    <header className="workspace-header">
      <div className={`wh-app-name${appNameLenClass}`} title={appName}>{appName}</div>
      <div className="wh-right">
        <div className="wh-search-wrap" ref={wrapRef} style={{ position: 'relative' }}>
          <svg className="wh-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            className="wh-search-input"
            type="text"
            placeholder="Search Tasks, People, Projects..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => { if (searchQuery.trim().length >= 2 && results) {} }}
            autoComplete="off"
          />
          {searchQuery ? (
            <button className="wh-search-clear" onClick={clearSearch} aria-label="Clear search">✕</button>
          ) : null}

          {/* Dropdown — width/position match the search input itself, computed
              from wrapRef's live bounding rect rather than a hardcoded width,
              so it never drifts out of alignment with the input above it. */}
          {(results !== null || searching) && searchQuery.trim().length >= 2 && (
            <div style={{
              position: 'fixed', top: 58, left: 'auto',
              minWidth: 320, maxHeight: 420, overflowY: 'auto',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              zIndex: 9999,
            }}
              ref={el => {
                if (el && wrapRef.current) {
                  const r = wrapRef.current.getBoundingClientRect();
                  el.style.width  = r.width + 'px';
                  el.style.left   = r.left + 'px';
                  el.style.top    = (r.bottom + 4) + 'px';
                }
              }}
            >
              {searching && (
                <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>
              )}
              {!searching && noResults && (
                <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-muted)' }}>No results for "{searchQuery}"</div>
              )}
              {!searching && hasResults && (
                <>
                  {results.tasks?.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Tasks</div>
                      <div style={{ maxHeight: 185, overflowY: 'auto' }}>
                        {results.tasks.map(t => (
                          <div key={t.id} onMouseDown={() => handleResultClick('task', t)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderRadius: 6, margin: '1px 4px' }}
                            className="gs-result-row">
                            {t.task_number && (
                              <span style={{ marginRight: 8, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t.task_number}</span>
                            )}
                            <span style={{ fontWeight: 500 }}>{t.title}</span>
                            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{t.status?.replace('_', ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {results.projects?.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', borderTop: results.tasks?.length ? '1px solid var(--border)' : 'none' }}>Projects</div>
                      {results.projects.map(p => (
                        <div key={p.id} onMouseDown={() => handleResultClick('project', p)}
                          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderRadius: 6, margin: '1px 4px' }}
                          className="gs-result-row">
                          <span style={{ fontWeight: 500 }}>{p.name}</span>
                          {p.client_name && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>{p.client_name}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {results.people?.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', borderTop: (results.tasks?.length || results.projects?.length) ? '1px solid var(--border)' : 'none' }}>People</div>
                      {results.people.map(p => (
                        <div key={p.id}
                          style={{ padding: '8px 14px', fontSize: 13, color: 'var(--text-primary)', borderRadius: 6, margin: '1px 4px' }}>
                          <span style={{ fontWeight: 500 }}>{personName(p)}</span>
                          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>{p.email}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <a
          href={SUPPORT_TICKET_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="wh-icon-btn"
          title="Contact Support"
          aria-label="Contact Support"
          style={{
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            width: 'auto',
            padding: '0 12px',
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Contact Support
        </a>
        <button onClick={toggleTheme} className="wh-icon-btn" title={theme === 'light' ? 'Dark mode' : 'Light mode'} aria-label="Toggle theme">
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <a href="/help.html" target="_blank" rel="noopener noreferrer" className="wh-icon-btn wh-help-btn" title="Help & Documentation" aria-label="Open documentation">
          <span style={{ fontSize: 16, lineHeight: 1 }}>?</span>
        </a>
        <NotificationBell appId={appId} />
        <div className="wh-profile" ref={profileRef}>
          <button
            id="tour-profile"
            type="button"
            className="wh-avatar-link"
            title="Account menu"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen(o => !o)}
          >
            <div className="wh-avatar">
              {user?.imageUrl
                ? <img src={user.imageUrl} alt={initials} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : initials}
            </div>
            <span className="wh-caret">▾</span>
          </button>
          {profileOpen && (
            <div className="wh-profile-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => goTo('/profile')}>Account &amp; Billing</button>
              {isAdmin && (
                <>
                  <div className="wh-profile-menu-sep" />
                  <button type="button" role="menuitem" onClick={() => goTo(`/apps/${appId}/setup/organisation`)}>Organisation Setup</button>
                  <button type="button" role="menuitem" onClick={() => goTo(`/apps/${appId}/users`)}>Users</button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// Module-level cache keyed by appId — survives re-renders and navigation,
// cleared only when the user switches workspaces or explicitly refreshes.
const wsCache = new Map(); // appId → { app, me, organisation }

export default function AppWorkspace() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoaded } = useUser();
  const { isSignedIn } = useAuth();
  const api = useApi();

  const cached = wsCache.get(appId);
  const [app, setApp] = useState(cached?.app || null);
  const [me, setMe] = useState(cached?.me || null);
  const [organisation, setOrganisation] = useState(cached?.organisation || null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!cached);
  const [searchQuery, setSearchQuery] = useState('');

  // Keep api in a ref so load() doesn't change identity when api reference changes
  const apiRef = useRef(api);
  apiRef.current = api;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [appRes, meRes, orgRes] = await Promise.all([
        apiRef.current.getApp(appId),
        apiRef.current.me(),
        apiRef.current.getOrganisation(appId).catch(() => null),
      ]);
      setApp(appRes);
      setMe(meRes);
      if (orgRes) setOrganisation(orgRes);
      wsCache.set(appId, { app: appRes, me: meRes, organisation: orgRes });
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || err.message || 'Failed to load app');
    } finally {
      if (!silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    // Only fetch when there is no cached data — cache is cleared on app switch.
    if (!wsCache.has(appId)) {
      load(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, appId]);

  useEffect(() => { setSearchQuery(''); }, [location.pathname]);

  const refreshOrganisation = useCallback(async () => {
    try {
      const org = await apiRef.current.getOrganisation(appId);
      setOrganisation(org);
      const prev = wsCache.get(appId);
      if (prev) wsCache.set(appId, { ...prev, organisation: org });
    } catch (_) {
      /* swallow — caller renders error */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  if (!isLoaded) return null;

  if (loading) {
    return (
      <div className="page-shell">
        <Header />
        <main className="page-main">
          <p>Loading workspace…</p>
        </main>
      </div>
    );
  }

  if (error === 'App deactivated') return null;

  if (error || !app) {
    return (
      <div className="page-shell">
        <Header />
        <main className="page-main">
          <button className="link-btn" onClick={() => navigate('/dashboard')}>
            ← Back to My Apps
          </button>
          <div className="banner banner-error">
            <strong>Couldn’t load:</strong> {error || 'App not found'}
          </div>
        </main>
      </div>
    );
  }

  const myRole = app.myRole;
  const ctx = {
    app,
    me,
    myRole,
    canWrite: myRole === 'super_admin' || myRole === 'admin',
    canManage: myRole === 'super_admin',
    organisation,
    refreshOrganisation,
    searchQuery,
    setSearchQuery,
  };

  return (
    <WorkspaceContext.Provider value={ctx}>
      <div className="workspace-shell">
        <AppTour isAdmin={myRole === 'super_admin' || myRole === 'admin'} appId={appId} />
        <Sidebar />
        <div className="workspace-right">
          <WorkspaceHeader searchQuery={searchQuery} setSearchQuery={setSearchQuery} appName={organisation?.name || app.name} />
          <main className="workspace-main">
            <Outlet />
          </main>
        </div>
      </div>
    </WorkspaceContext.Provider>
  );
}

export function DefaultWorkspaceRoute() {
  return <Navigate to="home" replace />;
}
